import { useMutation, useQuery } from '@tanstack/react-query';
import { Dayjs } from 'dayjs';
import {
  validateSession,
  getReservationServices,
  getHairdresserSchedule,
  getCustomerVisits,
  createCustomerVisit,
  getMyVisits,
  type ValidateSessionResponse,
  type HairdresserScheduleResponse,
  type CreateCustomerVisitResponse,
  activateClient,
  ActivateClientResponse,
  ActivationTokenResponse,
  confirmDeviceLink,
  ConfirmDeviceLinkResponse,
  createActivationToken,
  createDeviceLink,
  DeviceLinkTokenResponse,
  ReservationServicesResponse,
  loginWithPin,
  LoginWithPinResponse,
  setClientPinSelf,
  revokeReservationAccess,
  requestPinReset,
  resetClientPin,
} from './api';
import { getCustomerAvailableSlots } from '@features/visits/schedule/api';
import { TimeSlot } from '@features/visits/schedule/entity';
import { useIntl } from 'react-intl';
import { useAddSnackbarMessage } from '@shared/useAddSnackBar';
import { ApiErr } from '@shared/entity';
import { queryClient } from '../../shared/queries';
import { useAnalytics } from '@libs/mixpanel/useAnalytics';

export const useCreateActivationTokenMutation = () => {
  const intl = useIntl();
  const addSnackbarMessage = useAddSnackbarMessage();
  const { trackOnboardingStep } = useAnalytics();

  return useMutation<ActivationTokenResponse, Error, string>({
    mutationFn: (clientId: string) => createActivationToken(clientId),
    onSuccess: () => {
      trackOnboardingStep('reservationEnabled', 9);
      queryClient.invalidateQueries({ queryKey: ['onboarding-progress'] });
      addSnackbarMessage({
        type: 'success',
        text: intl.formatMessage({ id: 'customerAuth.snackbar.qrGenerated' }),
      });
    },
    onError: error => {
      addSnackbarMessage({
        type: 'error',
        text: error.message || intl.formatMessage({ id: 'customerAuth.snackbar.qrGenerateFailed' }),
      });
    },
  });
};

export const useRevokeReservationAccessMutation = () => {
  const intl = useIntl();
  const addSnackbarMessage = useAddSnackbarMessage();

  return useMutation<void, Error, string>({
    mutationFn: (clientId: string) => revokeReservationAccess(clientId),
    onSuccess: (_, clientId) => {
      queryClient.invalidateQueries({ queryKey: ['client', clientId] });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['onboarding-progress'] });
      addSnackbarMessage({
        type: 'success',
        text: intl.formatMessage({
          id: 'customerAuth.snackbar.revokeSuccess',
          defaultMessage: 'Online rezervace byly zakazány.',
        }),
      });
    },
    onError: error => {
      addSnackbarMessage({
        type: 'error',
        text:
          error.message ||
          intl.formatMessage({
            id: 'customerAuth.snackbar.revokeFailed',
            defaultMessage: 'Nepodařilo se zakázat online rezervace.',
          }),
      });
    },
  });
};

export const useActivateClientMutation = () => {
  const intl = useIntl();
  const addSnackbarMessage = useAddSnackbarMessage();

  return useMutation<ActivateClientResponse, Error, string>({
    mutationFn: (token: string) => activateClient(token),
    onSuccess: () => {
      addSnackbarMessage({
        type: 'success',
        text: intl.formatMessage({ id: 'customerAuth.snackbar.activationSuccess' }),
      });
    },
    onError: error => {
      addSnackbarMessage({
        type: 'error',
        text: error.message || intl.formatMessage({ id: 'customerAuth.snackbar.activationFailed' }),
      });
    },
  });
};

export const useValidateSessionMutation = () => {
  return useMutation<ValidateSessionResponse, Error, string>({
    mutationFn: () => validateSession(),
  });
};

export const useCreateDeviceLinkMutation = () => {
  const intl = useIntl();
  const addSnackbarMessage = useAddSnackbarMessage();

  return useMutation<DeviceLinkTokenResponse, Error, string>({
    mutationFn: () => createDeviceLink(),
    onSuccess: () => {
      addSnackbarMessage({
        type: 'success',
        text: intl.formatMessage({ id: 'customerAuth.snackbar.deviceLinkCreated' }),
      });
    },
    onError: error => {
      addSnackbarMessage({
        type: 'error',
        text:
          error.message ||
          intl.formatMessage({ id: 'customerAuth.snackbar.deviceLinkCreateFailed' }),
      });
    },
  });
};

export const useConfirmDeviceLinkMutation = () => {
  const intl = useIntl();
  const addSnackbarMessage = useAddSnackbarMessage();

  return useMutation<ConfirmDeviceLinkResponse, Error, string>({
    mutationFn: (linkToken: string) => confirmDeviceLink(linkToken),
    onSuccess: () => {
      addSnackbarMessage({
        type: 'success',
        text: intl.formatMessage({ id: 'customerAuth.snackbar.deviceLinkSuccess' }),
      });
    },
    onError: error => {
      addSnackbarMessage({
        type: 'error',
        text: error.message || intl.formatMessage({ id: 'customerAuth.snackbar.deviceLinkFailed' }),
      });
    },
  });
};

export const useValidateSessionQuery = (enabled = true) => {
  return useQuery<ValidateSessionResponse, Error>({
    queryKey: ['validateSession'],
    queryFn: () => validateSession(),
    enabled: enabled,
    staleTime: 1000 * 60 * 5,
    retry: false,
  });
};

export const useReservationServicesQuery = (enabled: boolean = true) => {
  return useQuery<ReservationServicesResponse, Error>({
    queryKey: ['reservationServices'],
    queryFn: () => getReservationServices(),
    enabled: enabled,
    staleTime: 1000 * 60 * 10,
    retry: 1,
  });
};

export const useHairdresserScheduleQuery = (
  enabled: boolean = true,
  year?: number,
  month?: number,
) => {
  return useQuery<HairdresserScheduleResponse, Error>({
    queryKey: ['hairdresserSchedule', year, month],
    queryFn: () => getHairdresserSchedule(year, month),
    enabled: enabled,
    staleTime: 1000 * 60 * 30,
    retry: 1,
  });
};

export const useCustomerVisitsQuery = (
  date: Dayjs | undefined,
  userId: string | undefined,
  enabled: boolean = true,
) => {
  const from = date?.startOf('month').format('YYYY-MM-DD') ?? '';
  const to = date?.endOf('month').format('YYYY-MM-DD') ?? '';

  return useQuery({
    queryKey: ['customerVisits', date],
    queryFn: () => {
      if (!userId) {
        throw new Error('Session token a userId jsou nezbytné!');
      }
      return getCustomerVisits(from, to, userId);
    },
    enabled: enabled && !!userId && !!date,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });
};

export const useMyVisitsQuery = (enabled: boolean = true) => {
  return useQuery({
    queryKey: ['myVisits'],
    queryFn: () => getMyVisits(),
    enabled: enabled,
    staleTime: 1000 * 60 * 2,
    retry: 1,
  });
};

export type CreateCustomerVisitData = {
  date?: Date;
  dateTo?: Date;
  serviceIds: string;
  depositRequired?: boolean;
  paidPrice?: number;
  deposit?: number;
};

export const useCreateCustomerVisitMutation = () => {
  const intl = useIntl();
  const addSnackbarMessage = useAddSnackbarMessage();

  return useMutation<CreateCustomerVisitResponse, ApiErr, CreateCustomerVisitData>({
    mutationFn: (visitData: CreateCustomerVisitData) => createCustomerVisit(visitData),
    onSuccess: () => {
      addSnackbarMessage({
        type: 'success',
        text: intl.formatMessage({ id: 'customerAuth.snackbar.reservationCreated' }),
      });
    },
    onError: (error: ApiErr) => {
      if (error.code === 409) {
        addSnackbarMessage({
          type: 'error',
          text: error.message,
        });
      } else {
        addSnackbarMessage({
          type: 'error',
          text: intl.formatMessage({ id: 'customerAuth.snackbar.reservationFailed' }),
        });
      }
    },
  });
};

export const useLoginWithPinMutation = () => {
  const intl = useIntl();
  const addSnackbarMessage = useAddSnackbarMessage();

  return useMutation<LoginWithPinResponse, Error, { phone: string; userId: string; pin: string }>({
    mutationFn: params => loginWithPin(params),
    onError: error => {
      addSnackbarMessage({
        type: 'error',
        text: error.message || intl.formatMessage({ id: 'customerAuth.loginFailed' }),
      });
    },
  });
};

export const useSetClientPinSelfMutation = () => {
  const intl = useIntl();
  const addSnackbarMessage = useAddSnackbarMessage();

  return useMutation<{ success: boolean }, Error, { pin: string; email: string }>({
    mutationFn: params => setClientPinSelf(params),
    onSuccess: () => {
      addSnackbarMessage({
        type: 'success',
        text: intl.formatMessage({
          id: 'customerAuth.snackbar.pinSaved',
          defaultMessage: 'PIN byl nastaven.',
        }),
      });
    },
    onError: error => {
      addSnackbarMessage({
        type: 'error',
        text:
          error.message ||
          intl.formatMessage({
            id: 'customerAuth.snackbar.pinFailed',
            defaultMessage: 'PIN se nepodařilo nastavit.',
          }),
      });
    },
  });
};

export const useRequestPinResetMutation = () => useMutation({ mutationFn: requestPinReset });
export const useResetClientPinMutation = () => useMutation({ mutationFn: resetClientPin });

export const useCustomerAvailableSlotsQuery = (
  date: string | undefined,
  serviceId: string | undefined,
  enabled = true,
) => {
  return useQuery<TimeSlot[], Error>({
    queryKey: ['customerAvailableSlots', date, serviceId],
    queryFn: () => getCustomerAvailableSlots(date!, serviceId!),
    enabled: enabled && !!date && !!serviceId,
    staleTime: 60 * 1000,
  });
};
